import {
  LIME_MATE_OPEN_IN_NEW_TAB_LABEL,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TOOL_LABELS,
  RIGHT_PANEL_TOOL_LIME_MATE,
  RIGHT_PANEL_TOOL_LIVE_TOTALS,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
} from 'constants/uiConstants';
import {
  FARMS_URL_PARAM_NODE,
  LIME_MATE_PAGE_PATH,
} from 'constants/routeConstants';
import { buildSiteNodeKey } from 'hooks/useSiteAutofill';
import { Spin, Tooltip } from 'antd';
import PlanActualSegmented from 'components/common/PlanActualSegmented';
import React, { forwardRef, Suspense, useCallback, useState } from 'react';
import { LuCalculator, LuExternalLink, LuX } from 'react-icons/lu';
import {
  PiChartBar,
  PiChartPie,
  PiCurrencyDollar,
  PiLayout,
  PiLeaf,
  PiEye,
  PiEyeSlash,
  PiPushPinSlash,
  PiPushPinSlashFill,
  PiRows,
  PiScales,
} from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeRightPanelTool,
  RightPanelTabId,
  RightPanelTabStatus,
  RightPanelTool,
  setRightPanelActivePinnedTab,
  setRightPanelActiveTool,
  setRightPanelCategoryFilter,
  setRightPanelChartVisible,
  setRightPanelTabOrder,
  toggleRightPanelSectionExpanded,
} from '../../redux/slices/InsightsSlice';
import { RootState } from '../../redux/Store';
import MergeSection from './MergeSection';
import PinnedTabControl from './PinnedTabControl';
import RightPanelTabRouter from './RightPanelTabRouter';

// Only loaded once someone opens LimeMate (OP-2383).
const LimeMatePanel = React.lazy(() => import('./panels/LimeMatePanel'));

const TOOL_OPTIONS: { value: RightPanelTool; label: string }[] = [
  RIGHT_PANEL_TOOL_LIVE_TOTALS,
  RIGHT_PANEL_TOOL_LIME_MATE,
].map((value) => ({ value, label: RIGHT_PANEL_TOOL_LABELS[value] }));

const TOOL_ICONS: Record<RightPanelTool, React.ReactElement> = {
  [RIGHT_PANEL_TOOL_LIVE_TOTALS]: <PiChartBar size={16} aria-hidden />,
  [RIGHT_PANEL_TOOL_LIME_MATE]: <LuCalculator size={16} aria-hidden />,
};

// LimeMate header actions — they sit on bg-gray-bright, so hover goes to white.
const LIME_MATE_HEADER_BUTTON_CLASS =
  'inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-gray-dark hover:bg-white hover:text-charcoal';

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={14} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={14} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={14} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={14} />,
};

// Every section's own content area is wrapped in p-4 (1rem each side).
const CONTENT_PADDING_REM = 2;
const getRootFontSize = (): number =>
  parseFloat(getComputedStyle(document.documentElement).fontSize);

interface RightPinnedPanelProps {
  width: number;
  tabs: Record<RightPanelTabId, RightPanelTabStatus>;
  tabOrder: RightPanelTabId[];
  pinnedTabIds: RightPanelTabId[];
  activePinnedTabId: RightPanelTabId | null;
  isMerged: boolean;
  mergedSectionExpanded: Record<RightPanelTabId, boolean>;
  onUnpin: (tabId: RightPanelTabId) => void;
  onUnpinAll: () => void;
  onMergeToggle: () => void;
}

/**
 * The fixed-width pinned panel column.
 *
 * Single tab pinned → content area, no tab control.
 * 2+ tabs, not merged → activePinnedTabId content + PinnedTabControl at bottom.
 * 2+ tabs, merged → MergeSection list filling the content area.
 *
 * Merge/unmerge toggle appears in the header whenever 2+ tabs are pinned.
 *
 * The header's tool selector (OP-2383) switches between the live totals tabs
 * above and the LimeMate calculator, which takes over the content area.
 * Once opened, the calculator stays mounted (hidden) while the panel exists,
 * so switching back to live totals and returning keeps its inputs.
 * Forwarded ref lets RightPanelShell reassert DOM width via useLayoutEffect.
 */
const RightPinnedPanel = forwardRef<HTMLDivElement, RightPinnedPanelProps>(
  (
    {
      width,
      tabs,
      tabOrder,
      pinnedTabIds,
      activePinnedTabId,
      isMerged,
      mergedSectionExpanded,
      onUnpin,
      onUnpinAll,
      onMergeToggle,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const chartVisible = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.chartVisible
    );
    const categoryFilter = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
    );
    const activeTool = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.activeTool
    );
    const isLimeMate = activeTool === RIGHT_PANEL_TOOL_LIME_MATE;
    // Farm/paddock in scope — autofills LimeMate, and travels to the new tab
    // in the same `node` URL param /farms uses.
    const selectedFarmId = useSelector(
      (state: RootState) => state.farmReducer.selectedFarmId
    );
    const selectedPaddockId = useSelector(
      (state: RootState) => state.farmReducer.selectedPaddockId
    );
    const siteNodeKey = buildSiteNodeKey(selectedFarmId, selectedPaddockId);
    const limeMateHref = siteNodeKey
      ? `${LIME_MATE_PAGE_PATH}?${new URLSearchParams({ [FARMS_URL_PARAM_NODE]: siteNodeKey }).toString()}`
      : LIME_MATE_PAGE_PATH;
    const [limeMateVisited, setLimeMateVisited] = useState(isLimeMate);
    if (isLimeMate && !limeMateVisited) setLimeMateVisited(true);
    const hasMultiple = pinnedTabIds.length >= 2;

    // Every pinned/merged section shares this exact content width, so one
    // check here predicts whether ANY of them would render as narrow — the
    // shared control only has an effect (a single-column fallback to pick
    // Plan or Actual for) when that's the case.
    const isWide =
      (width - CONTENT_PADDING_REM) * getRootFontSize() >=
      RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTabSelect = useCallback(
      (tabId: RightPanelTabId) => {
        dispatch(setRightPanelActivePinnedTab(tabId));
      },
      [dispatch]
    );

    /**
     * PinnedTabControl emits a new pinned-only order.
     * Map it back into the full tabOrder, keeping non-pinned tabs in place.
     */
    const handleReorder = useCallback(
      (newPinnedOrder: RightPanelTabId[]) => {
        const pinnedPositions = tabOrder
          .map((id, idx) => ({ id, idx }))
          .filter(({ id }) => tabs[id] === 'pinned')
          .map(({ idx }) => idx);

        const newTabOrder = [...tabOrder];
        pinnedPositions.forEach((pos, i) => {
          newTabOrder[pos] = newPinnedOrder[i];
        });

        dispatch(setRightPanelTabOrder(newTabOrder));
      },
      [dispatch, tabOrder, tabs]
    );

    const handleSectionToggle = useCallback(
      (tabId: RightPanelTabId) => {
        dispatch(toggleRightPanelSectionExpanded(tabId));
      },
      [dispatch]
    );

    const handleMoveSection = useCallback(
      (tabId: RightPanelTabId, direction: 'up' | 'down') => {
        const fromIdx = pinnedTabIds.indexOf(tabId);
        const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
        if (toIdx < 0 || toIdx >= pinnedTabIds.length) return;

        const newPinned = [...pinnedTabIds];
        [newPinned[fromIdx], newPinned[toIdx]] = [
          newPinned[toIdx],
          newPinned[fromIdx],
        ];
        handleReorder(newPinned);
      },
      [pinnedTabIds, handleReorder]
    );

    // In non-merged mode the header reflects the active tab.
    // In merged mode it uses a generic label.
    const headerTabId = isMerged || isLimeMate ? null : activePinnedTabId;

    // Used by the merged-mode Show/Hide all charts button.
    const allChartsVisible = pinnedTabIds.every((id) => chartVisible[id]);

    return (
      <div
        ref={ref}
        style={{ width: `${width}rem` }}
        className="flex flex-shrink-0 flex-col overflow-hidden border-l bg-white pt-16"
      >
        {/* ── Tool tabs (OP-2383) ────────────────────────────────────────────
            Full-width tabs rather than an inline control, so switching
            between live totals and a calculator reads as navigation. Both tabs
            share one style; only the active state differs. */}
        <div
          role="tablist"
          aria-label="Right panel tool"
          className="flex border-b border-gray-argent bg-gray-smoke"
        >
          {TOOL_OPTIONS.map(({ value, label }) => {
            const isActive = activeTool === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => dispatch(setRightPanelActiveTool(value))}
                className={[
                  'relative flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 px-2 text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-white text-charcoal after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-success'
                    : 'text-gray-dark hover:bg-gray-bright hover:text-charcoal',
                ].join(' ')}
              >
                {TOOL_ICONS[value]}
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div
          className={`flex items-center justify-between gap-2 border-b px-3 py-2 ${isLimeMate ? 'border-gray-argent bg-gray-bright' : 'bg-gray-50'}`}
        >
          {isLimeMate ? (
            <span className="truncate text-sm text-gray-dark">
              Lime rate calculator
            </span>
          ) : headerTabId ? (
            <div
              className="flex min-w-0 cursor-pointer select-none items-center gap-1.5"
              onDoubleClick={() => onUnpin(headerTabId)}
            >
              {TAB_ICONS[headerTabId]}
              <span className="truncate text-xs font-semibold uppercase tracking-wider text-charcoal">
                {RIGHT_PANEL_TAB_LABELS[headerTabId]}
                {headerTabId === RIGHT_PANEL_TAB_FINANCIAL && (
                  <span className="ml-1 font-semibold normal-case tracking-normal text-secondary-dark">
                    (Plan data only)
                  </span>
                )}
              </span>
            </div>
          ) : (
            <span className="truncate text-sm text-gray-dark">
              All pinned panels
            </span>
          )}

          {isLimeMate ? (
            <div className="flex items-center gap-1">
              <Tooltip title={LIME_MATE_OPEN_IN_NEW_TAB_LABEL}>
                <a
                  href={limeMateHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${LIME_MATE_OPEN_IN_NEW_TAB_LABEL} (LimeMate)`}
                  className={LIME_MATE_HEADER_BUTTON_CLASS}
                >
                  <LuExternalLink size={14} aria-hidden />
                </a>
              </Tooltip>
              <Tooltip title={<>Close LimeMate<br /><span className="opacity-75">Return to live totals</span></>}>
                <button
                  type="button"
                  aria-label="Close LimeMate"
                  onClick={() => dispatch(closeRightPanelTool())}
                  className={LIME_MATE_HEADER_BUTTON_CLASS}
                >
                  <LuX size={14} aria-hidden />
                </button>
              </Tooltip>
            </div>
          ) : (
          <div className="flex items-center gap-1">
            {/* Merge toggle — only when 2+ tabs are pinned */}
            {hasMultiple && (
              <Tooltip
                title={
                  isMerged ? (
                    <>Unmerge panels<br /><span className="opacity-75">Show each panel as its own tab</span></>
                  ) : (
                    <>Merge panels<br /><span className="opacity-75">Stack all pinned panels into one view</span></>
                  )
                }
              >
                <button
                  type="button"
                  aria-label={isMerged ? 'Unmerge panels' : 'Merge panels'}
                  onClick={onMergeToggle}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  {isMerged ? <PiLayout size={14} /> : <PiRows size={14} />}
                </button>
              </Tooltip>
            )}

            {/* Show / hide chart */}
            {isMerged && hasMultiple ? (
              <Tooltip
                title={
                  allChartsVisible ? (
                    <>Hide all charts<br /><span className="opacity-75">Collapse charts in all merged panels</span></>
                  ) : (
                    <>Show all charts<br /><span className="opacity-75">Display charts in all merged panels</span></>
                  )
                }
              >
                <button
                  type="button"
                  aria-label={allChartsVisible ? 'Hide all charts' : 'Show all charts'}
                  onClick={() =>
                    pinnedTabIds.forEach((id) =>
                      dispatch(setRightPanelChartVisible({ tabId: id, visible: !allChartsVisible }))
                    )
                  }
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  {allChartsVisible ? <PiEyeSlash size={14} /> : <PiEye size={14} />}
                </button>
              </Tooltip>
            ) : (
              headerTabId && (
                <Tooltip
                  title={
                    chartVisible[headerTabId] ? (
                      <>Hide chart<br /><span className="opacity-75">Collapse the chart for this panel</span></>
                    ) : (
                      <>Show chart<br /><span className="opacity-75">Display the chart for this panel</span></>
                    )
                  }
                >
                  <button
                    type="button"
                    aria-label={chartVisible[headerTabId] ? 'Hide chart' : 'Show chart'}
                    onClick={() =>
                      dispatch(
                        setRightPanelChartVisible({
                          tabId: headerTabId,
                          visible: !chartVisible[headerTabId],
                        })
                      )
                    }
                    className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                  >
                    {chartVisible[headerTabId] ? (
                      <PiEyeSlash size={14} />
                    ) : (
                      <PiEye size={14} />
                    )}
                  </button>
                </Tooltip>
              )
            )}

            {/* Unpin all (merged) or unpin active tab (non-merged) */}
            {isMerged && hasMultiple ? (
              <Tooltip title={<>Unpin all<br /><span className="opacity-75">Collapse all panels to the edge strip</span></>}>
                <button
                  type="button"
                  aria-label="Unpin all"
                  onClick={onUnpinAll}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  <PiPushPinSlashFill size={14} />
                </button>
              </Tooltip>
            ) : (
              activePinnedTabId && (
                <Tooltip title={<>Unpin panel<br /><span className="opacity-75">Return this panel to the edge strip</span></>}>
                  <button
                    type="button"
                    aria-label="Unpin panel"
                    onClick={() => onUnpin(activePinnedTabId)}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                  >
                    <PiPushPinSlash size={14} />
                  </button>
                </Tooltip>
              )
            )}
          </div>
          )}
        </div>

        {/* ── Shared Plan/Actual control ───────────────────────────────────────
            One control for every pinned/merged section (OP-2333) — narrow
            sections read this instead of each carrying its own toggle. Only
            shown when the panel is actually narrow enough to need it — at
            full width every section already shows Plan and Actual side by
            side, so the control would have nothing to do. */}
        {!isWide && !isLimeMate && (
          <div className="flex items-center justify-between border-b bg-white px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-muted">
              Category
            </span>
            <PlanActualSegmented
              value={categoryFilter}
              onChange={(cat) => dispatch(setRightPanelCategoryFilter(cat))}
            />
          </div>
        )}

        {/* ── Content area ──────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {limeMateVisited && (
            <div className={isLimeMate ? 'min-h-full bg-gray-bright p-4' : 'hidden'}>
              <Suspense
                fallback={
                  <div className="flex justify-center py-8">
                    <Spin />
                  </div>
                }
              >
                <LimeMatePanel siteNodeKey={siteNodeKey} />
              </Suspense>
            </div>
          )}
          {isLimeMate ? null : isMerged && hasMultiple ? (
            /* Merged mode: all pinned tabs as collapsible sections */
            pinnedTabIds.map((tabId, idx) => (
              <MergeSection
                key={tabId}
                tabId={tabId}
                isExpanded={mergedSectionExpanded[tabId]}
                isFirst={idx === 0}
                isLast={idx === pinnedTabIds.length - 1}
                onToggle={() => handleSectionToggle(tabId)}
                onMoveUp={() => handleMoveSection(tabId, 'up')}
                onMoveDown={() => handleMoveSection(tabId, 'down')}
              >
                <div className="p-4">
                  <RightPanelTabRouter tabId={tabId} />
                </div>
              </MergeSection>
            ))
          ) : (
            /* Single or non-merged: active tab content */
            <div className="p-4">
              {activePinnedTabId && (
                <RightPanelTabRouter tabId={activePinnedTabId} />
              )}
            </div>
          )}
        </div>

        {/* ── Tab switcher — only when 2+ pinned and not merged ─────────────── */}
        {hasMultiple && !isMerged && !isLimeMate && (
          <PinnedTabControl
            pinnedTabIds={pinnedTabIds}
            activePinnedTabId={activePinnedTabId}
            panelWidth={width}
            onTabSelect={handleTabSelect}
            onUnpin={onUnpin}
            onReorder={handleReorder}
          />
        )}
      </div>
    );
  }
);

RightPinnedPanel.displayName = 'RightPinnedPanel';

export default RightPinnedPanel;
